import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ChevronDown, Send, MessageCircle, Clock, Shield, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: 'Hoe maak ik een account aan?',
    answer:
      'Klik op "Registreren" op de startpagina, kies of je student of verhuurder bent en volg de stappen. Je ontvangt een verificatie-e-mail om je account te activeren.',
  },
  {
    question: 'Is StudyStay gratis voor studenten?',
    answer:
      'Ja! StudyStay is volledig gratis voor studenten. Je kunt kamers zoeken, favorieten opslaan, berichten sturen en bezichtigingen aanvragen zonder kosten.',
  },
  {
    question: 'Hoe plan ik een bezichtiging?',
    answer:
      'Ga naar een kamer die je interesseert en klik op "Bezichtiging aanvragen". Kies een beschikbaar tijdslot en de verhuurder ontvangt je aanvraag. Je krijgt een bevestiging zodra het is goedgekeurd.',
  },
  {
    question: 'Hoe werkt het huurcontract?',
    answer:
      'Zodra jij en de verhuurder akkoord gaan, wordt het contract digitaal aangemaakt in StudyStay. Beide partijen ondertekenen elektronisch. Je kunt je contract altijd terugvinden onder "Contracten".',
  },
  {
    question: 'Kan ik mijn huurcontract aanpassen?',
    answer:
      'Wijzigingen aan het contract kunnen worden aangevraagd via de amendment-functie. De verhuurder moet elke wijziging goedkeuren voordat deze van kracht wordt.',
  },
  {
    question: 'Hoe neem ik contact op met mijn verhuurder?',
    answer:
      'Via de ingebouwde chat kun je rechtstreeks berichten sturen naar je verhuurder. Ga naar "Berichten" om een gesprek te starten of verder te gaan.',
  },
  {
    question: 'Wat als ik een probleem heb met mijn kamer?',
    answer:
      'Neem eerst contact op met je verhuurder via de chat. Komt het niet tot een oplossing? Stuur ons dan een e-mail via het formulier op deze pagina en we helpen je verder.',
  },
  {
    question: 'Hoe kan ik mijn account verwijderen?',
    answer:
      'Ga naar je profiel, scroll naar beneden en klik op "Account verwijderen". Al je gegevens worden permanent verwijderd conform onze privacybeleid.',
  },
];

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const mailtoLink = `mailto:info@studystay.org?subject=${encodeURIComponent(
      formData.subject || 'Contact via StudyStay'
    )}&body=${encodeURIComponent(
      `Naam: ${formData.name}\nE-mail: ${formData.email}\n\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;

    setTimeout(() => {
      setSending(false);
      setSent(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    }, 600);
  };

  return (
    <div className="min-h-screen bg-[var(--market-bg)]">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#5EABEB] via-[#4C9DE0] to-[#7F87E6]">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white/15 blur-2xl animate-float-slow" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-white/10 blur-2xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/5 blur-3xl" />
        </div>

        <div className="relative z-10 container-professional py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-white/90 text-sm font-medium mb-6">
            <MessageCircle className="w-4 h-4" />
            We helpen je graag
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Neem contact op
          </h1>
          <p className="text-base md:text-lg text-blue-50/90 max-w-xl mx-auto">
            Heb je een vraag, suggestie of probleem? We staan klaar om je te helpen.
          </p>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-[var(--market-bg)]" />
      </div>

      {/* Quick info cards */}
      <div className="container-professional -mt-6 relative z-20 mb-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Mail, label: 'E-mail', value: 'info@studystay.org', href: 'mailto:info@studystay.org' },
            { icon: Clock, label: 'Reactietijd', value: 'Binnen 24 uur' },
            { icon: Shield, label: 'Privacy', value: 'Jouw data is veilig', to: '/legal/privacy-policy' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-[var(--shadow-soft)] border border-[var(--surface-border)] p-5 flex items-center gap-4 hover:shadow-[var(--shadow-hover)] hover:-translate-y-0.5 transition-all duration-200">
              <div className="w-11 h-11 rounded-xl bg-[#EAF4FE] flex items-center justify-center shrink-0">
                <item.icon className="w-5 h-5 text-[#5EABEB]" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{item.label}</p>
                {item.href ? (
                  <a href={item.href} className="text-sm font-semibold text-slate-800 hover:text-[#2B6FA6] transition-colors truncate block">
                    {item.value}
                  </a>
                ) : item.to ? (
                  <Link to={item.to} className="text-sm font-semibold text-slate-800 hover:text-[#2B6FA6] transition-colors truncate block">
                    {item.value}
                  </Link>
                ) : (
                  <p className="text-sm font-semibold text-slate-800">{item.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ + Contact form */}
      <div className="container-professional pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* FAQ */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#EAF4FE] flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-[#5EABEB]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Veelgestelde vragen</h2>
                <p className="text-sm text-slate-500">Misschien staat je antwoord er al tussen</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl border transition-all duration-200 ${
                    openFaq === index
                      ? 'border-[#5EABEB]/35 shadow-[var(--shadow-hover)]'
                      : 'border-[var(--surface-border)] shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] hover:border-[#5EABEB]/20'
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left gap-4"
                  >
                    <span className="text-sm font-semibold text-slate-800">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${
                        openFaq === index ? 'rotate-180 text-[#5EABEB]' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      openFaq === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5 pt-0">
                      <div className="h-px bg-slate-100 mb-4" />
                      <p className="text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-[var(--shadow-soft)] border border-[var(--surface-border)] p-6 md:p-8 sticky top-28">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#EAF4FE] flex items-center justify-center">
                  <Send className="w-5 h-5 text-[#5EABEB]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Stuur een bericht</h2>
                  <p className="text-sm text-slate-500">We reageren zo snel mogelijk</p>
                </div>
              </div>

              {sent && (
                <div className="mb-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-emerald-700 font-medium">
                    Je e-mailclient is geopend. Verzend het bericht om ons te bereiken!
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Naam</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Je volledige naam"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">E-mailadres</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="je@email.com"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Onderwerp</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Waar gaat het over?"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Bericht</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Beschrijf je vraag of probleem..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none resize-none placeholder:text-slate-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35"
                >
                  {sending ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Verstuur bericht
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Nolf Technologies OÜ</p>
                    <p className="text-xs text-slate-400">Tornimäe tn 5, 10145 Tallinn, Estonia</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
